import { useEffect } from "react";
import { useTranslation } from "react-i18next";

const SITE_URL = "https://employguard.de";
const LANGS = ["en", "de", "bs"];
const OG_LOCALE = { en: "en_US", de: "de_DE", bs: "bs_BA" };

function upsertMeta(attr, key, content) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(rel, href, hreflang) {
  const sel = hreflang ? `link[rel="${rel}"][hreflang="${hreflang}"]` : `link[rel="${rel}"]`;
  let el = document.head.querySelector(sel);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    if (hreflang) el.setAttribute("hreflang", hreflang);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function upsertJsonLd(id, obj) {
  let el = document.head.querySelector(`script[data-ld="${id}"]`);
  if (!obj) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement("script");
    el.setAttribute("type", "application/ld+json");
    el.setAttribute("data-ld", id);
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(obj);
}

export function Seo({ titleKey, descKey, title, desc, path = "/", schema = null, faq = null, breadcrumb = null }) {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || "en").slice(0, 2);
  const schemaStr = schema ? JSON.stringify(schema) : "";
  const faqStr = faq ? JSON.stringify(faq) : "";
  const bcStr = breadcrumb ? JSON.stringify(breadcrumb) : "";
  useEffect(() => {
    const title_ = title || t(titleKey);
    const desc_ = desc || t(descKey);
    document.documentElement.lang = lang;
    document.title = title_;
    upsertMeta("name", "description", desc_);
    upsertMeta("property", "og:title", title_);
    upsertMeta("property", "og:description", desc_);
    upsertMeta("property", "og:locale", OG_LOCALE[lang] || "en_US");
    upsertMeta("property", "og:url", `${SITE_URL}${path}`);
    upsertLink("canonical", `${SITE_URL}${path}`);
    LANGS.forEach((l) => upsertLink("alternate", `${SITE_URL}${path}?lng=${l}`, l));
    upsertLink("alternate", `${SITE_URL}${path}`, "x-default");
    upsertJsonLd("org", {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "EmployGuard",
      url: SITE_URL,
      logo: `${SITE_URL}/favicon.png`,
      description: t("seo.homeD"),
      sameAs: ["https://www.sandysmajic.com"],
      contactPoint: { "@type": "ContactPoint", email: "info@employguard.de", contactType: "customer support" },
    });
    upsertJsonLd("website", {
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: "EmployGuard",
      url: SITE_URL,
      inLanguage: LANGS,
    });
    upsertJsonLd("page", schema);
    upsertJsonLd("faq", faq && faq.length ? {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faq.map((it) => ({
        "@type": "Question",
        name: it.q,
        acceptedAnswer: { "@type": "Answer", text: it.a },
      })),
    } : null);
    upsertJsonLd("breadcrumb", breadcrumb && breadcrumb.length ? {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: breadcrumb.map((b, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: b.name,
        item: `${SITE_URL}${b.path}`,
      })),
    } : null);
  }, [t, lang, titleKey, descKey, title, desc, path, schemaStr, faqStr, bcStr]);
  return null;
}

export default Seo;
